import { IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ActionsCell } from './cells/ActionsCell';
import { NameCell } from './cells/NameCell';
import { PermissionsCell } from './cells/PermissionsCell';
import { StatusCell } from './cells/StatusCell';
import styles from './UsersTable.module.css';
import { TIntroducerListItem } from './UsersTable.types';

const columnHelper = createColumnHelper<TIntroducerListItem>();

const columns = [
  columnHelper.display({
    id: 'name',
    header: 'Name',
    size: 200,
    cell: ({ row }) => <NameCell user={row.original} />,
  }),
  columnHelper.accessor('is_active', {
    id: 'status',
    header: 'Status',
    size: 150,
    cell: (info) => <StatusCell isActive={info.getValue()} />,
  }),
  columnHelper.accessor('permissions', {
    id: 'permissions',
    header: 'Account permissions',
    size: 350,
    cell: (info) => <PermissionsCell permissions={info.getValue()} />,
  }),
  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
    size: 300,
    cell: (info) => info.getValue() ?? '',
  }),
  columnHelper.accessor('phone_number', {
    id: 'phone',
    header: 'Phone',
    size: 150,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 100,
    cell: ({ row }) => <ActionsCell user={row.original} />,
  }),
];

type TProps = {
  users: TIntroducerListItem[];
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isLoading: boolean;
  currentIntroducerId: string | null;
};

export const UsersTable = ({
  users,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  currentIntroducerId,
}: TProps) => {
  // TODO: when column sort/filter lands, unpin the self row whenever a sort or
  // filter is active so it follows the applied order.
  const pinnedTopIds =
    currentIntroducerId && users.some((user) => user.id === currentIntroducerId)
      ? [currentIntroducerId]
      : [];

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (user) => user.id,
    keepPinnedRows: true,
    state: { rowPinning: { top: pinnedTopIds } },
  });

  const rows = [...table.getTopRows(), ...table.getCenterRows()];

  return (
    <div className={styles.wrapper}>
      <Input
        type="search"
        aria-label="Search by name or email"
        placeholder="Search by name or email"
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        wrapperClassName={styles.search}
        iconRight={
          <IconZoomOutline aria-hidden className={styles.searchIcon} />
        }
      />
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <colgroup>
            {table.getHeaderGroups()[0].headers.map((header) => (
              <col key={header.id} style={{ width: header.getSize() }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.header}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className={styles.emptyState}>
                  <Spinner dataTestId="users-table-spinner" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.emptyState}>
                  {searchQuery ? 'No users match your search' : 'No users yet'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={styles.row}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={styles.cell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
